import { extend } from "@dhx/ts-common/core";
import { create, inject, el } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { DataCollection, TreeCollection, DataEvents, IDataEventsHandlersMap } from "@dhx/ts-data";
import { View } from "@dhx/ts-common/view";
import { IPagination, IPaginationConfig, PaginationEvents, IPaginationEventHandlersMap } from "./types";
import { Toolbar } from "@dhx/ts-toolbar";
import { Id } from "@dhx/ts-common/types";

export class Pagination extends View implements IPagination {
	public config: IPaginationConfig;
	public events: IEventSystem<
		DataEvents | PaginationEvents,
		IDataEventsHandlersMap & IPaginationEventHandlersMap
	>;
	public data: DataCollection;

	private _toolbar: Toolbar;
	private _page: number;
	private _size: number;

	constructor(container: HTMLElement | string, config: IPaginationConfig) {
		super(container, extend({ page: 0, pageSize: 10, inputWidth: 40 }, config));

		this.data = this.config.data;

		this._page = -1;
		this._size = this.config.pageSize;

		this.events = new EventSystem<
			DataEvents | PaginationEvents,
			IDataEventsHandlersMap & IPaginationEventHandlersMap
		>(this);

		this._initUI();
		this._initEvents();

		const view = create({
			render: () => this._render(),
		});

		this.mount(container, view);
		this.setPage(this.config.page);
	}

	paint() {
		this._updateSizeUI();
	}

	destructor(): void {
		this._toolbar.destructor();
		this.events.clear();
		this.unmount();
	}

	setPage(page: number, necessarily = false): void {
		const index = Math.max(0, Math.min(page, this.getPagesCount() - 1));
		const previousIndex = this._page;
		if (index !== previousIndex || necessarily) {
			this._page = index;

			const start = this._page * this._size;
			this.data.setRange(start, start + this._size);
			this._toolbar.data.update("count", { value: (index + 1).toString() });

			this.paint();
			this.events.fire(PaginationEvents.change, [index, previousIndex]);
			this.data.events.fire(DataEvents.change, [
				undefined,
				"setPage",
				[index * this._size, previousIndex * this._size],
			]);
		} else {
			this.paint();
		}
	}

	getPage(): number {
		return this._page;
	}

	setPageSize(size: number): void {
		this._size = size;
		this.setPage(this._page, true);
	}

	getPageSize(): number {
		return this._size;
	}

	getPagesCount(): number {
		return Math.ceil(this.data.getLength() / this._size);
	}

	private _showItem(index: number) {
		this.setPage(Math.floor(index / this._size));
	}

	private _showTreeItem(id: Id) {
		const item = this.data.getItem(id);
		if (this.data.getItem(item.parent)) {
			this._showTreeItem(item.parent);
		} else {
			this.setPage(Math.floor(this.data.getIndex(id) / this._size));
		}
	}

	private _updateSizeUI() {
		this._toolbar.data.update("size", { value: `/ ${this.getPagesCount()}` });
	}

	private _initUI() {
		this._toolbar = new Toolbar(null, {
			data: [
				{
					id: "first",
					type: "button",
					icon: "dxi dxi-chevron-double-left",
					tooltip: "First",
					view: "link",
					circle: true,
				},
				{
					id: "previous",
					type: "button",
					icon: "dxi dxi-chevron-left",
					tooltip: "Previous",
					view: "link",
					circle: true,
				},
				{
					id: "count",
					type: "input",
					width: this.config.inputWidth,
					css: "dhx_pagination__count-panel",
				},
				{
					id: "size",
					type: "title",
					css: "dhx_pagination__count-panel",
				},
				{
					id: "next",
					type: "button",
					icon: "dxi dxi-chevron-right",
					tooltip: "Next",
					view: "link",
					circle: true,
				},
				{
					id: "last",
					type: "button",
					icon: "dxi dxi-chevron-double-right",
					tooltip: "Last",
					view: "link",
					circle: true,
				},
			],
		});

		this._updateSizeUI();
	}

	private _initEvents() {
		this.data.events.on("focusChange", (index, id) => {
			if (this.data instanceof TreeCollection) {
				this._showItem(index);
			} else {
				this._showTreeItem(id);
			}
		});

		this.data.events.on(DataEvents.change, () => {
			this.setPage(this._page);
		});

		this._toolbar.events.on("click", id => {
			let page;
			switch (id) {
				case "previous":
					page = this._page - 1;
					break;
				case "next":
					page = this._page + 1;
					break;
				case "first":
					page = 0;
					break;
				case "last":
					page = Infinity;
					break;
			}
			if (page !== undefined) {
				this.setPage(page);
			}
		});

		this._toolbar.events.on("inputFocus", id => {
			const input = document.querySelector(
				`.dhx_pagination[data-dhx-widget-id=${this._uid}] .dhx_input`
			) as HTMLInputElement;
			if (input) {
				input.select();
			}
		});

		this._toolbar.events.on("inputBlur", id => {
			const input = document.querySelector(
				`.dhx_pagination[data-dhx-widget-id=${this._uid}] .dhx_input`
			) as HTMLInputElement;
			if (input && !input.value) {
				input.value = (this._page + 1).toString();
			}
		});

		this._toolbar.events.on(DataEvents.change, id => {
			if (id === "count") {
				const value = parseInt(this._toolbar.data.getItem(id).value);
				if (!isNaN(value)) {
					this.setPage(value - 1);
				}
			}
		});
	}

	private _render() {
		return el(
			".dhx_widget.dhx_pagination",
			{
				class: this.config.css,
				"data-dhx-widget-id": this._uid,
				_key: this._uid,
			},
			[inject(this._toolbar.getRootView())]
		);
	}
}

// protected _renderClassic(): VNode {
// 	let pages = this.getPagesCount() - 1;
// 	const now = this._page;
// 	const list = this.config.listLength;
//
// 	let from = pages >= list ? Math.max(0, Math.round(now - this.config.listLength / 2)) : 0;
// 	let to = from + this.config.listLength - 1;
//
// 	if (to > pages) {
// 		from -= to - pages;
// 		to -= to - pages;
// 	}
// 	if (from < 0) from = to = pages = 0;
//
// 	const first = el(
// 		`div.dhx_pagination_button${now == 0 ? ".dhx_pagination_empty" : ""}`,
// 		{ "data-id": "first" },
// 		"<<"
// 	);
// 	const prev = el(
// 		`div.dhx_pagination_button${now == 0 ? ".dhx_pagination_empty" : ""}`,
// 		{ "data-id": "prev" },
// 		"<"
// 	);
// 	const next = el(
// 		`div.dhx_pagination_button${now == pages ? ".dhx_pagination_empty" : ""}`,
// 		{ "data-id": "next" },
// 		">"
// 	);
// 	const last = el(
// 		`div.dhx_pagination_button${now == pages ? ".dhx_pagination_empty" : ""}`,
// 		{ "data-id": "last" },
// 		">>"
// 	);
// 	const options = range(from, to).map(i =>
// 		el(
// 			`div.dhx_pagination_button${i == now ? ".dhx_pagination_active" : ""}`,
// 			{ "data-id": i },
// 			(i + 1).toString()
// 		)
// 	);
//
// 	return el("div.ul.dhx_widget.dhx_pagination_classic", this._c_handlers, [
// 		first,
// 		prev,
// 		...options,
// 		next,
// 		last,
// 	]);
// }
//
// protected _render(): VNode {
// 	const pages = this.getPagesCount() - 1;
// 	const now = this._page;
//
// 	const first = el(
// 		`div.dhx_pagination_button${now == 0 ? ".dhx_pagination_empty" : ""}`,
// 		{ "data-id": "first" },
// 		"<<"
// 	);
// 	const prev = el(
// 		`div.dhx_pagination_button${now == 0 ? ".dhx_pagination_empty" : ""}`,
// 		{ "data-id": "prev" },
// 		"<"
// 	);
// 	const next = el(
// 		`div.dhx_pagination_button${now == pages ? ".dhx_pagination_empty" : ""}`,
// 		{ "data-id": "next" },
// 		">"
// 	);
// 	const last = el(
// 		`div.dhx_pagination_button${now == pages ? ".dhx_pagination_empty" : ""}`,
// 		{ "data-id": "last" },
// 		">>"
// 	);
// 	const options = [
// 		el("input.dhx_pagination_input", {
// 			value: now + 1,
// 			_key: uid(),
// 			onchange: this._handlers.onchange,
// 		}),
// 		` / ${pages + 1}`,
// 	];
//
// 	return el(
// 		"div.dhx_widget.dhx_pagination_modern",
// 		{
// 			onclick: this._handlers.onclick,
// 		},
// 		[this._toolbar.getRootView()]
// 	);
// }
